<template>
  <div class="w-full text-sm">
    <div
      v-if="$slots.header"
      class="mb-4"
    >
      <slot name="header" />
    </div>
    <dl class="flex flex-col w-full border-surface-weak border rounded-lg">
      <div
        v-for="index in count"
        :key="index"
        class="group flex flex-col border-b first:rounded-t-md last:rounded-b-md last:border-0 transition-colors"
        :class="[
          !expandedItems.includes(index - 1) ? 'bg-surface' : 'bg-surface-light',
        ]"
      >
        <div
          class="flex w-full flex-row items-center justify-between p-4"
          :class="[
            noClick || (noClickButPrevious && expandedItems[0] < index) ? 'pointer-events-none' : '',
            !expandedItems.includes(index - 1) ? 'cursor-pointer' : '',
          ]"
          @click="!expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? toggleItem(index - 1) : undefined"
        >
          <div class="flex items-center">
            <div
              :class="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? 'cursor-pointer' : 'pointer-events-none'"
              @click.stop="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? toggleItem(index - 1) : undefined"
            >
              <slot
                :name="`icon-left-${index - 1}`"
                :is_expanded="expandedItems.includes(index - 1)"
                :index="index - 1"
              >
                <slot
                  name="icon-left"
                  :is_expanded="expandedItems.includes(index - 1)"
                  :index="index - 1"
                >
                  <button class="mr-4 md:mr-6 size-6 outline-offset-2 rounded-full focus:outline-brand">
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 26 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0 13C0 5.8203 5.8203 0 13 0C20.1797 0 26 5.8203 26 13C26 20.1797 20.1797 26 13 26C5.8203 26 0 20.1797 0 13ZM17.8137 10.5812C18.1347 10.1318 18.0307 9.50728 17.5812 9.18627C17.1318 8.86526 16.5073 8.96935 16.1863 9.41876L11.8724 15.4582L9.70711 13.2929C9.31658 12.9024 8.68342 12.9024 8.29289 13.2929C7.90237 13.6834 7.90237 14.3166 8.29289 14.7071L11.2929 17.7071C11.5007 17.915 11.7895 18.0208 12.0825 17.9966C12.3754 17.9723 12.6429 17.8204 12.8137 17.5812L17.8137 10.5812Z"
                        fill="#A8A8A8"
                        class="opacity-80 transition-opacity duration-300 group-hover:opacity-100"
                      />
                    </svg>
                  </button>
                </slot>
              </slot>
            </div>

            <div
              :class="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? 'cursor-pointer' : 'pointer-events-none'"
              @click.stop="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? toggleItem(index - 1) : undefined"
            >
              <slot
                :name="`title-${index - 1}`"
                :index="index - 1"
              >
                <slot
                  name="title"
                  :index="index - 1"
                >
                  <span class="text-2xl">Title {{ index }}</span>
                </slot>
              </slot>
            </div>
          </div>

          <div
            :class="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? 'cursor-pointer' : 'pointer-events-none'"
            @click.stop="expandedItems.includes(index - 1) && !noClick && !(noClickButPrevious && expandedItems[0] < index) ? toggleItem(index - 1) : undefined"
          >
            <slot
              :name="`icon-right-${index - 1}`"
              :is_expanded="expandedItems.includes(index - 1)"
              :index="index - 1"
            >
              <slot
                name="icon-right"
                :is_expanded="expandedItems.includes(index - 1)"
                :index="index - 1"
              >
                <button class="ml-auto mr-0 outline-offset-2 rounded-full focus:outline-brand">
                  <svg
                    :class="expandedItems.includes(index - 1) ? '' : 'rotate-180'"
                    class="transition-transform"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.1515 10.3515C15.6201 9.88284 16.3799 9.88284 16.8485 10.3515L23.6485 17.1515C24.1172 17.6201 24.1172 18.3799 23.6485 18.8485C23.1799 19.3172 22.4201 19.3172 21.9515 18.8485L16 12.8971L10.0485 18.8485C9.5799 19.3172 8.8201 19.3172 8.35147 18.8485C7.88284 18.3799 7.88284 17.6201 8.35147 17.1515L15.1515 10.3515Z"
                      fill="#222222"
                    />
                  </svg>
                </button>
              </slot>
            </slot>
          </div>
        </div>

        <div
          v-show="expandedItems.includes(index - 1)"
          class="p-4 pt-0 pb-12 space-y-6 xl:px-4 2xl:px-12"
        >
          <slot
            :name="`content-${index - 1}`"
            :index="index - 1"
          >
            <slot
              name="content"
              :index="index - 1"
            >
              <div class="text-sm">
                Content {{ index }}
              </div>
            </slot>
          </slot>
        </div>
      </div>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  count: number
  defaultExpandedIndexes?: number[]
  allowMultiple?: boolean
  noClick?: boolean
  noClickButPrevious?: boolean
}>(), {
  count: 1,
  defaultExpandedIndexes: () => [],
  allowMultiple: true,
  noClick: false,
})

const emit = defineEmits<{
  (e: 'change', indexes: number[]): void
}>()
const expandedItems = ref<number[]>([...props.defaultExpandedIndexes])

const toggleItem = (index: number) => {
  if (expandedItems.value.includes(index)) {
    expandedItems.value = expandedItems.value.filter(i => i !== index)
  }
  else {
    if (props.allowMultiple) {
      expandedItems.value.push(index)
    }
    else {
      expandedItems.value = [index]
    }
  }
  emit('change', expandedItems.value)
}

watch(() => props.defaultExpandedIndexes, (newVal) => {
  expandedItems.value = [...newVal]
}, { deep: true })

defineExpose({
  expandedItems,
  toggleItem,
})
</script>
